/**
 * Pure function that recomposes an EquipmentTabResponse-shaped object from
 * client-side data sources, replacing the deleted backend
 * `/equipment-tab` aggregation endpoint (spec 2026-05-05-03).
 *
 * Inputs are kept simple and explicit so the function is easy to unit-test:
 *   - the parent asset's name + type code (passed in from the caller)
 *   - the descendant equipment list (from useAssetEquipment)
 *   - the asset-types list (from useAssetTypes — used to resolve trade UIDs and labels)
 *   - the derived-property cards (from useAssetDerivedProperties)
 *
 * Some legacy behaviors are intentionally omitted to keep the migration
 * focused; the calling code can layer them on top:
 *   - location_label resolution (would need a per-equipment ancestry walk)
 *   - free-text `q` filter (would need indexing on name + properties)
 *   - per-trade equipment-row sorting beyond what the conditionRollup needs
 */

import type {
  EquipmentTabResponse,
  EquipmentTradeBucket,
  EquipmentRow as ApiEquipmentRow,
  EquipmentContractRef,
  EquipmentCondition,
  DerivedPropertyCard,
} from '../../api/equipment-tab-types'
import type { AssetEquipmentItem } from '../../hooks/useAssetEquipment'
import type { AssetInspection } from '../../hooks/useComplianceApi'
import { mapEquipmentCondition, computeConditionRollup } from './conditionRollup'
import { SPEC_TRADE_CODES } from './tradeOrder'
import { assetTypeAllowsMultiple, type AssetTypeNode } from '../asset-type-inheritance'
import { lotBaseTypeCode } from '../asset-type-tree'

type AssetTypeLite = {
  uid: string
  code: string
  name?: unknown
  emoji?: string | null
  icon?: string | null
  kind?: string
  inherits_from_asset_type_uid?: string | null
  /** Type flags (`multiple`, …) — read through the inheritance chain. */
  properties?: Record<string, unknown> | null
  /** Equipment ontology fields — used here for the theoretical design life. */
  equipment_properties?: {
    lifespan_reference?: Array<{ theoretical_lifespan_years?: number | null }>
  } | null
}

const CURRENT_YEAR = new Date().getFullYear()

/**
 * Container types that own their equipment and act as a rollup *boundary*:
 * equipment inside one of these does not surface on an ancestor's Equipment tab.
 *   - `building` — a building owns its equipment; it must not roll up to the
 *     enclosing site (`building_group`).
 *   - `unit` — a private lot ("Lot"); its equipment must not roll up to the
 *     building / floor / common area.
 * A boundary only stops rollup when it sits *strictly between* the equipment and
 * the viewed root, so a boundary asset viewed directly still shows its own
 * equipment (e.g. a building shows its roof; a lot shows its own equipment).
 */
const BOUNDARY_TYPE_CODES = new Set<string>(['building', 'unit'])

/**
 * A contract as returned by `GET /contracts?include=assets,contract_type`.
 * Only the fields the trade table needs are modelled.
 */
export interface ContractLite {
  uid: string
  name?: string | null
  contract_type?: { code?: string | null } | null
  assets?: ReadonlyArray<{ asset_uid?: string | null }> | null
}

/**
 * A marché de travaux is what a warranty hangs off (garantie de parfait
 * achèvement / décennale): the ontology has no dedicated warranty contract
 * type, so `WORK_RENOVATION` is the entity a warranty is attributable to.
 */
const WARRANTY_CONTRACT_TYPE_CODE = 'WORK_RENOVATION'

/**
 * Which column a linked contract lights up, from its type code.
 *
 * The contractor family is `CONTRACTING_WORK` (« Travaux de sous-traitance »)
 * and its `WORK_*` children. Every member but the renovation marché is a
 * standing contractor relationship — entretien, service, contrôle
 * réglementaire — i.e. who maintains the equipment. The bare parent code is
 * deliberately in the maintenance bucket: an imported maintenance contract
 * is typed `CONTRACTING_WORK` far more often than `WORK_MAINTENANCE_*`
 * (the import guidance only recently learnt the sub-types), and showing it
 * under Garantie read as "no maintenance contract". Insurance, leases,
 * mandates and loans say nothing about who maintains a thing, so they light
 * neither column.
 */
function contractColumn(code: string | null | undefined): 'maintenance' | 'warranty' | null {
  if (!code) return null
  if (code === WARRANTY_CONTRACT_TYPE_CODE) return 'warranty'
  if (code === 'CONTRACTING_WORK' || code.startsWith('WORK_') || code === 'SUPPLY') return 'maintenance'
  return null
}

interface ContractIndex {
  maintenance: Map<string, EquipmentContractRef>
  warranty: Map<string, EquipmentContractRef>
}

/**
 * Index the asset-subtree contracts by the equipment UID they are linked to.
 * When several contracts of the same kind target one equipment, the first one
 * wins — the column shows one link, not a list.
 */
function indexContractsByAsset(contracts: ReadonlyArray<ContractLite>): ContractIndex {
  const maintenance = new Map<string, EquipmentContractRef>()
  const warranty = new Map<string, EquipmentContractRef>()
  for (const contract of contracts) {
    const column = contractColumn(contract.contract_type?.code)
    if (!column) continue
    const target = column === 'warranty' ? warranty : maintenance
    const ref: EquipmentContractRef = { uid: contract.uid, name: contract.name || contract.uid }
    for (const link of contract.assets ?? []) {
      const assetUid = link?.asset_uid
      if (!assetUid || target.has(assetUid)) continue
      target.set(assetUid, ref)
    }
  }
  return { maintenance, warranty }
}

interface EquipmentTabFiltersInput {
  conditions?: ReadonlyArray<EquipmentCondition>
  trades?: ReadonlyArray<string>
  /** Free-text search; applied to name + asset_type_uid resolved label. */
  q?: string
}

interface RecomposeArgs {
  /** UID of the asset whose Equipment tab we're rendering. */
  assetUid: string
  /** Asset name for the response header. */
  assetName: string
  /** Asset-type code for the response header (e.g. "building_group"). */
  assetTypeCode: string
  equipment: ReadonlyArray<AssetEquipmentItem>
  assetTypes: ReadonlyArray<AssetTypeLite>
  derived: ReadonlyArray<DerivedPropertyCard>
  /**
   * Contracts linked anywhere in the viewed asset's subtree, with their
   * `assets` + `contract_type` includes hydrated. Drives the Maintenance /
   * Garantie columns. Defaults to none.
   */
  contracts?: ReadonlyArray<ContractLite>
  /**
   * Periodic inspections of the subtree (`GET /assets/{uid}/compliance/inspections
   * ?include_descendants=true`), spread over the rows by `asset_uid`. Drives
   * the Vérifications column. Defaults to none.
   */
  inspections?: ReadonlyArray<AssetInspection>
  filters?: EquipmentTabFiltersInput
}

function localized(name: unknown, fallback: string, lang = 'fr'): string {
  if (typeof name === 'string') return name
  if (name && typeof name === 'object') {
    const m = name as Record<string, string>
    return m[lang] || m.en || m.fr || fallback
  }
  return fallback
}

function isTradeKind(at: AssetTypeLite): boolean {
  if (at.kind === 'trade') return true
  if (at.kind) return false
  return at.code.startsWith('trade_')
}

function isEquipmentKind(at: AssetTypeLite): boolean {
  return at.kind === 'equipment'
}

function pickProperty(item: AssetEquipmentItem, code: string): unknown {
  const props = item.properties ?? []
  for (const p of props) {
    if (p.code === code) return p.value
  }
  return undefined
}

function sortTrades(trades: AssetTypeLite[]): AssetTypeLite[] {
  return [...trades].sort((a, b) => {
    const ai = SPEC_TRADE_CODES.indexOf(a.code as never)
    const bi = SPEC_TRADE_CODES.indexOf(b.code as never)
    if (ai !== -1 && bi !== -1) return ai - bi
    if (ai !== -1) return -1
    if (bi !== -1) return 1
    return a.code.localeCompare(b.code)
  })
}

export function recomposeEquipmentTab(args: RecomposeArgs): EquipmentTabResponse {
  const { assetUid, assetName, assetTypeCode, equipment, assetTypes, derived, contracts, inspections, filters } = args

  const contractIndex = indexContractsByAsset(contracts ?? [])
  const inspectionsByAsset = new Map<string, AssetInspection[]>()
  for (const item of inspections ?? []) {
    const list = inspectionsByAsset.get(item.asset_uid) ?? []
    list.push(item)
    inspectionsByAsset.set(item.asset_uid, list)
  }

  const assetTypeByUid = new Map<string, AssetTypeLite>()
  for (const at of assetTypes) assetTypeByUid.set(at.uid, at)

  // Name lookup for the "rattachement" (parent asset). The equipment list holds
  // every descendant, so an equipment's parent (a floor / common area / building)
  // is usually in it; the root asset itself is not, so seed it explicitly.
  const nameByUid = new Map<string, string>()
  nameByUid.set(assetUid, assetName)
  // Ancestry maps to enforce the rollup-boundary rule: equipment owned by a
  // nested building or lot does not surface on an ancestor's Equipment tab.
  const parentByUid = new Map<string, string | null>()
  const typeCodeByUid = new Map<string, string>()
  for (const item of equipment) {
    if (!item.uid) continue
    if (item.name) nameByUid.set(item.uid, item.name)
    parentByUid.set(item.uid, item.parent_asset_uid ?? null)
    const code = item.asset_type_uid ? assetTypeByUid.get(item.asset_type_uid)?.code : undefined
    if (code) typeCodeByUid.set(item.uid, code)
  }

  // True when a boundary container (building / lot) sits strictly between the
  // equipment and the viewed root — i.e. the equipment belongs to a nested
  // building or lot and must not roll up here. When the viewed root IS that
  // boundary, it is not an in-between ancestor, so its own equipment stays.
  const belowNestedBoundary = (item: AssetEquipmentItem): boolean => {
    let cur = item.parent_asset_uid ?? null
    const seen = new Set<string>()
    while (cur && cur !== assetUid && !seen.has(cur)) {
      seen.add(cur)
      if (BOUNDARY_TYPE_CODES.has(lotBaseTypeCode(typeCodeByUid.get(cur)))) return true
      cur = parentByUid.get(cur) ?? null
    }
    return false
  }

  const allTrades = sortTrades(assetTypes.filter(isTradeKind))

  // Resolve each equipment to its trade UID (via the inherits_from chain).
  const equipmentByTradeUid = new Map<string, AssetEquipmentItem[]>()
  for (const item of equipment) {
    if (!item.asset_type_uid) continue
    const at = assetTypeByUid.get(item.asset_type_uid)
    if (!at) continue
    if (!isEquipmentKind(at)) continue
    if (belowNestedBoundary(item)) continue // nested building/lot equipment doesn't roll up
    const tradeUid = at.inherits_from_asset_type_uid
    if (!tradeUid) continue
    const arr = equipmentByTradeUid.get(tradeUid) ?? []
    arr.push(item)
    equipmentByTradeUid.set(tradeUid, arr)
  }

  // Apply filters (conditions, trades, q) — affects the *visible* slice.
  const conditionFilter = filters?.conditions && filters.conditions.length > 0
    ? new Set(filters.conditions)
    : null
  const tradeFilter = filters?.trades && filters.trades.length > 0
    ? new Set(filters.trades)
    : null
  const qLower = filters?.q?.toLowerCase().trim() || ''

  // Build rollups from the population (all trades, unaffected by the view
  // filters) — but still excluding private-lot equipment, so the trade chips
  // match the rows shown.
  const rollups = computeConditionRollup(
    equipment
      .filter((item) => !belowNestedBoundary(item))
      .map((item) => {
        const at = item.asset_type_uid ? assetTypeByUid.get(item.asset_type_uid) : undefined
        const tradeUid = at?.inherits_from_asset_type_uid ?? null
        return {
          tradeUid,
          conditionRaw: pickProperty(item, 'equipment_condition'),
        }
      }),
  )

  // Build one row per eligible equipment, filters evaluated but not applied
  // yet: a component's match must reach its assembly (spec 2026-10-27-17).
  const tradeUidByUid = new Map<string, string>()
  const itemByUid = new Map<string, AssetEquipmentItem>()
  for (const [tradeUid, items] of equipmentByTradeUid) {
    for (const item of items) {
      tradeUidByUid.set(item.uid, tradeUid)
      itemByUid.set(item.uid, item)
    }
  }
  const rowByUid = new Map<string, ApiEquipmentRow>()
  const matchesByUid = new Map<string, boolean>()
  for (const item of itemByUid.values()) {
    const conditionRaw = pickProperty(item, 'equipment_condition')
    const condition = mapEquipmentCondition(conditionRaw)
    const itemAt = item.asset_type_uid ? assetTypeByUid.get(item.asset_type_uid) : undefined
    const typeLabel = itemAt ? localized(itemAt.name, itemAt.code) : null
    const name = item.name || ''
    let matches = !conditionFilter || conditionFilter.has(condition)
    if (matches && qLower) {
      const haystack = `${name} ${typeLabel ?? ''}`.toLowerCase()
      matches = haystack.includes(qLower)
    }
    matchesByUid.set(item.uid, matches)
    // Commissioning date (assets.start_date — the source of truth since spec
    // 2026-09-03-02, which retired the `commissioning_year` property) +
    // theoretical design life (from the type's lifespan_reference) → the
    // age/lifespan progress bar.
    const startDate = item.start_date ?? null
    const commissioningYear = startDate
      ? Number.parseInt(startDate.slice(0, 4), 10) || null
      : null
    const installedAt = startDate
    const lifespanYears =
      itemAt?.equipment_properties?.lifespan_reference?.[0]?.theoretical_lifespan_years ?? null
    const ageYears = commissioningYear ? Math.max(0, CURRENT_YEAR - commissioningYear) : null
    // Rattachement: the parent asset (floor / common area / building) the
    // equipment is attached to — not necessarily the building itself.
    const locationLabel = item.parent_asset_uid ? nameByUid.get(item.parent_asset_uid) ?? null : null
    rowByUid.set(item.uid, {
      id: item.uid,
      name,
      type_label: typeLabel,
      location_label: locationLabel,
      installed_at: installedAt,
      lifespan_years: lifespanYears,
      age_years: ageYears,
      condition,
      // Whether the type (or an ancestor) lets one asset stand for several
      // units — what makes the Nombre cell editable, exactly when the API
      // accepts `quantity > 1`.
      multiple: assetTypeAllowsMultiple(assetTypes as AssetTypeNode[], item.asset_type_uid),
      quantity: item.quantity ?? null,
      start_date: startDate,
      has_condition_value: conditionRaw !== null && conditionRaw !== undefined && conditionRaw !== '',
      maintenance_contract: contractIndex.maintenance.get(item.uid) ?? null,
      warranty_contract: contractIndex.warranty.get(item.uid) ?? null,
      inspections: inspectionsByAsset.get(item.uid) ?? [],
    })
  }

  // Components: a row whose `part_of` target is listed here nests under it.
  const assemblyOf = assemblyIndex(itemByUid)
  const componentsOf = new Map<string, string[]>()
  for (const [uid, asm] of assemblyOf) {
    const list = componentsOf.get(asm) ?? []
    list.push(uid)
    componentsOf.set(asm, list)
  }
  // A component without a maintenance contract of its own is covered by its
  // assembly's, the nearest one up the chain.
  for (const [uid, row] of rowByUid) {
    if (row.maintenance_contract) continue
    const seen = new Set<string>([uid])
    let asm = assemblyOf.get(uid)
    while (asm && !seen.has(asm)) {
      seen.add(asm)
      const contract = contractIndex.maintenance.get(asm)
      if (contract) {
        row.maintenance_contract = contract
        row.maintenance_via = nameByUid.get(asm) ?? null
        break
      }
      asm = assemblyOf.get(asm)
    }
  }
  const filterActive = !!conditionFilter || !!qLower
  // Returns the row with its visible components, or null when neither it nor
  // any component matches.
  const nest = (uid: string, seen: Set<string>): ApiEquipmentRow | null => {
    const row = rowByUid.get(uid)
    if (!row || seen.has(uid)) return null
    seen.add(uid)
    const self = matchesByUid.get(uid) ?? false
    const children: ApiEquipmentRow[] = []
    let childMatched = false
    for (const c of componentsOf.get(uid) ?? []) {
      // Under a matching assembly every component shows; otherwise only the
      // branches that match.
      const nested = self ? nestAll(c, seen) : nest(c, seen)
      if (nested) {
        children.push(nested)
        if (!self) childMatched = true
        if (nested.components_expanded) childMatched = true
      }
    }
    if (!self && children.length === 0) return null
    const out: ApiEquipmentRow = { ...row }
    if (children.length > 0) {
      out.components = children
      if (filterActive && childMatched) out.components_expanded = true
    }
    if (assemblyOf.has(uid)) out.assembly_uid = assemblyOf.get(uid)
    return out
  }
  const nestAll = (uid: string, seen: Set<string>): ApiEquipmentRow | null => {
    const row = rowByUid.get(uid)
    if (!row || seen.has(uid)) return null
    seen.add(uid)
    const children = (componentsOf.get(uid) ?? [])
      .map((c) => nestAll(c, seen))
      .filter((r): r is ApiEquipmentRow => r !== null)
    const out: ApiEquipmentRow = { ...row, assembly_uid: assemblyOf.get(uid) ?? null }
    if (children.length > 0) out.components = children
    return out
  }

  const buckets: EquipmentTradeBucket[] = []
  for (const trade of allTrades) {
    if (tradeFilter && !tradeFilter.has(trade.code)) continue
    // A component is listed under its assembly, never in its own trade too.
    const items = (equipmentByTradeUid.get(trade.uid) ?? []).filter((item) => !assemblyOf.has(item.uid))
    const tradeLabel = localized(trade.name, trade.code)
    const visibleItems: ApiEquipmentRow[] = []
    for (const item of items) {
      const row = nest(item.uid, new Set<string>())
      if (row) visibleItems.push(row)
    }
    const rollup = rollups.get(trade.uid) ?? { poor: 0, end_of_life: 0, unassessed: 0, total: 0 }
    buckets.push({
      trade_code: trade.code,
      trade_uid: trade.uid,
      trade_label: tradeLabel,
      trade_emoji: trade.emoji ?? null,
      trade_icon: trade.icon ?? null,
      count: items.length,
      condition_rollup: {
        poor: rollup.poor,
        end_of_life: rollup.end_of_life,
        unassessed: rollup.unassessed,
      },
      equipment: visibleItems,
      descendant_asset_type_uids: [],
    })
  }

  let tradesWithAtRisk = 0
  for (const b of buckets) {
    if (b.condition_rollup.poor > 0 || b.condition_rollup.end_of_life > 0) {
      tradesWithAtRisk += 1
    }
  }

  return {
    asset: {
      id: assetUid,
      type: assetTypeCode,
      name: assetName,
    },
    derived_properties: [...derived],
    equipment_by_trade: buckets,
    totals: {
      equipment_count: equipment.length,
      trades_with_at_risk: tradesWithAtRisk,
    },
  }
}

/** The relation code a component joins its equipment with. */
export const PART_OF_RELATION = 'part_of'

/**
 * component uid → assembly uid, for every listed equipment whose `part_of`
 * target is listed too (spec 2026-10-27-17). A cycle (bad data) is cut so
 * every chain ends on a top-level row.
 */
export function assemblyIndex(itemByUid: ReadonlyMap<string, AssetEquipmentItem>): Map<string, string> {
  const out = new Map<string, string>()
  for (const item of itemByUid.values()) {
    const rel = (item.relations?.outgoing ?? []).find(
      (r) => r.relation_type === PART_OF_RELATION && r.target?.uid && itemByUid.has(r.target.uid),
    )
    if (rel?.target && rel.target.uid !== item.uid) out.set(item.uid, rel.target.uid)
  }
  for (const start of [...out.keys()]) {
    const seen = new Set<string>([start])
    let cur = out.get(start)
    while (cur) {
      if (seen.has(cur)) {
        out.delete(start)
        break
      }
      seen.add(cur)
      cur = out.get(cur)
    }
  }
  return out
}
